/**
 * What the document discloses, gathered from its tags.
 *
 * The reason the tags exist. A sustainability statement is read by somebody
 * looking for one requirement, and without this the only way to answer "where
 * is `E1-6`" is to read the document. This turns the tags into the two things
 * that question needs:
 *
 *   - an **index**, in the order a statement presents its standards, so a
 *     reader or an auditor can go straight to a requirement;
 *   - a **coverage check**, which is the more valuable of the two, because it
 *     answers the question nobody thinks to ask: what has been declared
 *     material and then not disclosed anywhere.
 *
 * Derived each time rather than stored, for the same reason the contents page
 * is: an index saved alongside a document is an index that goes quietly wrong
 * the first time somebody moves a chapter, and nobody notices until it is
 * printed.
 *
 * Pure. Takes sections, returns findings.
 */

import {
  compareRefs,
  parseEsrsRef,
  standardOrder,
  type EsrsPillar,
  type EsrsStandard,
  type Materiality,
} from '@/domain/esrs'
import type { Section } from '@/domain/section'

/** One tagged section, flattened out of the tree. */
export interface Disclosure {
  readonly sectionId: string
  readonly title: string
  readonly ref: string
  readonly standard: EsrsStandard
  readonly pillar: EsrsPillar
  readonly materiality: Materiality
}

/** Everything tagged to one standard. */
export interface StandardEntry {
  readonly standard: EsrsStandard
  readonly pillar: EsrsPillar
  readonly disclosures: readonly Disclosure[]
}

/**
 * Every tagged section, in document order.
 *
 * Document order rather than reference order, because this is also what the
 * coverage check counts, and a reader following the document meets them as
 * they come. The index sorts its own copy.
 */
export function disclosuresOf(sections: readonly Section[]): readonly Disclosure[] {
  const found: Disclosure[] = []

  function walk(list: readonly Section[]): void {
    for (const section of list) {
      const tag = section.tag
      if (tag) {
        const parsed = parseEsrsRef(tag.ref)
        if (parsed) {
          found.push({
            sectionId: section.id,
            title: section.title,
            ref: parsed.ref,
            standard: parsed.standard,
            pillar: parsed.pillar,
            materiality: tag.materiality,
          })
        }
      }

      if (section.kind === 'group') walk(section.children)
    }
  }

  walk(sections)
  return found
}

/**
 * The index: the disclosures grouped by standard, both in ESRS order.
 *
 * Standards with nothing tagged to them are left out. An index listing all
 * eleven with most of them empty says less than one listing what is actually
 * there, and the empty ones are the coverage check's business rather than the
 * index's.
 */
export function disclosureIndex(sections: readonly Section[]): readonly StandardEntry[] {
  const byStandard = new Map<EsrsStandard, Disclosure[]>()

  for (const disclosure of disclosuresOf(sections)) {
    const list = byStandard.get(disclosure.standard)
    if (list) list.push(disclosure)
    else byStandard.set(disclosure.standard, [disclosure])
  }

  return [...byStandard.entries()]
    .sort(([a], [b]) => standardOrder(a) - standardOrder(b))
    .map(([standard, disclosures]) => ({
      standard,
      pillar: disclosures[0]?.pillar ?? 'general',
      disclosures: [...disclosures].sort((a, b) => compareRefs(a.ref, b.ref)),
    }))
}

/** Something the document says about itself that does not hold up. */
export interface Gap {
  readonly kind: 'declaredNotDisclosed' | 'duplicated'
  readonly ref: string
  readonly standard: EsrsStandard
  /** The sections involved, for a duplicate. Empty for a missing disclosure. */
  readonly sectionIds: readonly string[]
}

/**
 * What does not hold up.
 *
 * Two findings, and neither is visible by reading the document:
 *
 * **Declared and not disclosed.** A standard tagged somewhere as material,
 * with no requirement of that standard tagged anywhere. This is the failure
 * worth catching: the statement asserts a topic matters and then says nothing
 * about it, which is exactly what an assurance provider looks for.
 *
 * **The same reference twice.** Two sections both claiming to answer `E1-6`.
 * Occasionally deliberate, usually the result of a chapter having been copied,
 * and always worth a second look before it is signed.
 *
 * What this deliberately does not do is check a reference against a list of
 * the requirements that exist. See the note in `esrs.ts`: this codebase does
 * not claim to know how many requirements E1 has, and inventing that list
 * would produce confident, wrong findings.
 */
export function gapsOf(sections: readonly Section[]): readonly Gap[] {
  const disclosures = disclosuresOf(sections)
  const gaps: Gap[] = []

  // A standard is material if anything tagged to it says so.
  const material = new Set<EsrsStandard>()
  for (const disclosure of disclosures) {
    if (disclosure.materiality === 'material') material.add(disclosure.standard)
  }

  // A standard is answered if something tagged to it names a requirement
  // rather than only the standard itself.
  const answered = new Set<EsrsStandard>()
  for (const disclosure of disclosures) {
    if (disclosure.ref.includes('-')) answered.add(disclosure.standard)
  }

  for (const standard of material) {
    if (answered.has(standard)) continue
    gaps.push({ kind: 'declaredNotDisclosed', ref: standard, standard, sectionIds: [] })
  }

  const byRef = new Map<string, Disclosure[]>()
  for (const disclosure of disclosures) {
    const list = byRef.get(disclosure.ref)
    if (list) list.push(disclosure)
    else byRef.set(disclosure.ref, [disclosure])
  }

  for (const [ref, found] of byRef) {
    if (found.length < 2) continue
    gaps.push({
      kind: 'duplicated',
      ref,
      standard: found[0]?.standard ?? 'ESRS2',
      sectionIds: found.map((disclosure) => disclosure.sectionId),
    })
  }

  return gaps
}
